import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalTitle, ModalContent, ModalFooter } from '../../../components/ui/Modal';
import { Button } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import api from '../../../services/api';

interface UserOption {
  id: number;
  userDisplayName?: string | null;
  userFirstName?: string;
  userLastName?: string;
  userEmail?: string;
}

interface ShareFolderModalProps {
  isOpen: boolean;
  onClose: () => void;
  folderId: number | null;
  folderName: string;
  onShared: () => void;
  shareFolder: (folderId: number, body: { sharedWithUserId: number; includeSubfolders?: boolean; permission?: 'read' | 'write' }) => Promise<void>;
}

const UserIcon = (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4 21a8 8 0 0116 0H4z" />
  </svg>
);

const FolderIcon = (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
  </svg>
);

const ShareFolderModal: React.FC<ShareFolderModalProps> = ({
  isOpen,
  onClose,
  folderId,
  folderName,
  onShared,
  shareFolder,
}) => {
  const { colors } = useTheme();
  const [users, setUsers] = useState<UserOption[]>([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
  const [includeSubfolders, setIncludeSubfolders] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setError(null);
      setUsersLoading(true);
      const loadData = async () => {
        try {
          const [usersRes, sharesRes] = await Promise.all([
            api.get<UserOption[]>('/users'),
            folderId != null ? api.get<Array<{ sharedWithUserId: number; includeSubfolders: boolean }>>(`/emails/personal-folders/${folderId}/shares`) : Promise.resolve({ data: [] }),
          ]);
          setUsers(Array.isArray(usersRes.data) ? usersRes.data : []);
          const shares = Array.isArray(sharesRes.data) ? sharesRes.data : [];
          if (shares.length > 0) {
            setSelectedUserIds(shares.map((s) => s.sharedWithUserId));
            setIncludeSubfolders(shares[0].includeSubfolders ?? true);
          } else {
            setSelectedUserIds([]);
            setIncludeSubfolders(true);
          }
        } catch {
          setUsers([]);
          setSelectedUserIds([]);
          setIncludeSubfolders(true);
        } finally {
          setUsersLoading(false);
        }
      };
      loadData();
    }
  }, [isOpen, folderId]);

  const toggleUser = (userId: number) => {
    setSelectedUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const handleSubmit = async () => {
    if (folderId == null || selectedUserIds.length === 0) return;
    setSaving(true);
    setError(null);
    try {
      for (const sharedWithUserId of selectedUserIds) {
        await shareFolder(folderId, {
          sharedWithUserId,
          includeSubfolders,
          permission: 'read',
        });
      }
      onShared();
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to share folder');
    } finally {
      setSaving(false);
    }
  };

  const userLabel = (u: UserOption) =>
    u.userDisplayName?.trim() ||
    [u.userFirstName, u.userLastName].filter(Boolean).join(' ') ||
    u.userEmail ||
    `User #${u.id}`;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <ModalHeader>
        <ModalTitle>Share folder</ModalTitle>
        <button
          type="button"
          onClick={onClose}
          className={cn('p-1.5 rounded-lg', colors.textMuted, colors.interactiveHover)}
          aria-label="Close"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </ModalHeader>
      <ModalContent>
        {folderName && (
          <div className={cn(
            'flex items-center gap-2.5 px-4 py-3 rounded-xl mb-4',
            'bg-black/5 dark:bg-white/5',
            colors.text
          )}>
            <span className={cn('opacity-70', colors.textMuted)}>{FolderIcon}</span>
            <div>
              <p className="text-xs font-medium uppercase tracking-wider opacity-60">Folder</p>
              <p className="text-sm font-medium truncate">{folderName}</p>
            </div>
          </div>
        )}

        <div className="space-y-4">
          <div>
            <h3 className={cn('text-sm font-semibold mb-2', colors.text)}>Who can see this folder</h3>
            <p className={cn('text-xs mb-3', colors.textMuted)}>
              Select users to share with. They will be able to view emails in this folder.
            </p>

            {usersLoading ? (
              <div className={cn('flex items-center justify-center py-12', colors.textMuted)}>
                <span className="animate-pulse">Loading users...</span>
              </div>
            ) : users.length === 0 ? (
              <div className={cn('rounded-xl border border-dashed py-10 text-center text-sm', colors.border, colors.textMuted)}>
                No users available to share with.
              </div>
            ) : (
              <div className={cn('rounded-xl border overflow-hidden', colors.border)}>
                <div className={cn(
                  'grid grid-cols-[1fr_auto] gap-3 px-4 py-2.5 text-xs font-medium uppercase tracking-wider',
                  colors.textMuted,
                  'bg-black/5 dark:bg-white/5'
                )}>
                  <span>User</span>
                  <span className="w-16 text-right">Status</span>
                </div>
                <div className="max-h-52 overflow-y-auto divide-y divide-gray-200 dark:divide-gray-700">
                  {users.map((u) => {
                    const selected = selectedUserIds.includes(u.id);
                    return (
                      <div
                        key={u.id}
                        className={cn(
                          'grid grid-cols-[1fr_auto] gap-3 items-center px-4 py-3 transition-colors',
                          selected && 'bg-indigo-50/50 dark:bg-indigo-900/20'
                        )}
                      >
                        <label className={cn(
                          'flex items-center gap-3 cursor-pointer min-w-0',
                          colors.text
                        )}>
                          <span className={cn(
                            'flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center [&>svg]:w-5 [&>svg]:h-5',
                            selected ? 'bg-indigo-100 dark:bg-indigo-900/40 text-indigo-600 dark:text-indigo-400' : 'bg-gray-100 dark:bg-gray-800 text-gray-400'
                          )}>
                            {UserIcon}
                          </span>
                          <input
                            type="checkbox"
                            checked={selected}
                            onChange={() => toggleUser(u.id)}
                            className="sr-only"
                          />
                          <span className="text-sm font-medium truncate flex-1">{userLabel(u)}</span>
                        </label>
                        <span className={cn(
                          'text-xs font-medium shrink-0 w-16 text-right',
                          selected ? 'text-indigo-600 dark:text-indigo-400' : colors.textMuted
                        )}>
                          {selected ? 'Shared' : '—'}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          <div className={cn(
            'flex items-center justify-between px-4 py-3 rounded-xl',
            'bg-black/5 dark:bg-white/5'
          )}>
            <div>
              <p className={cn('text-sm font-medium', colors.text)}>Include subfolders</p>
              <p className={cn('text-xs', colors.textMuted)}>Share all nested folders with selected users</p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={includeSubfolders}
                onChange={(e) => setIncludeSubfolders(e.target.checked)}
                className="sr-only peer"
              />
              <div className={cn(
                'relative w-11 h-6 rounded-full peer-checked:bg-indigo-600',
                'bg-gray-200 dark:bg-gray-700',
                "after:content-[''] after:absolute after:top-0.5 after:left-0.5 after:rounded-full after:h-5 after:w-5 after:bg-white after:shadow-sm after:transition-all peer-checked:after:translate-x-5"
              )} />
              <span className="ml-2 sr-only">Include subfolders</span>
            </label>
          </div>
        </div>

        {error && (
          <div className={cn(
            'mt-4 flex items-center gap-2 px-4 py-3 rounded-xl text-sm',
            'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400'
          )}>
            <svg className="w-5 h-5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            {error}
          </div>
        )}
      </ModalContent>
      <ModalFooter>
        <Button variant="secondary" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="default"
          onClick={handleSubmit}
          disabled={saving || selectedUserIds.length === 0 || folderId == null}
        >
          {saving ? 'Sharing...' : `Share with ${selectedUserIds.length} user${selectedUserIds.length !== 1 ? 's' : ''}`}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default ShareFolderModal;
